import * as React from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ContractFamilyPartyCorrection,
  MetadataPatchPayload,
  MetadataResponse,
  ReviewQueueItem,
} from "@/types/contract-family";

interface FormValues {
  documentName: string;
  parties: string;
  partyRoles: string;
  executionDate: string;
  effectiveDate: string;
  amendsReference: string;
  parentHints: string;
  filenameHints: string;
  multiDoc: "unknown" | "yes" | "no";
  comment: string;
}

interface Props {
  open: boolean;
  item: ReviewQueueItem | null;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: MetadataPatchPayload) => Promise<void>;
}

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

const partiesList = (value: unknown): ContractFamilyPartyCorrection[] =>
  Array.isArray(value)
    ? value.flatMap(item => {
        if (typeof item !== "object" || item === null || !("name" in item) || typeof item.name !== "string") {
          return [];
        }
        const role = "role" in item && typeof item.role === "string" ? item.role : null;
        return [{ name: item.name, role }];
      })
    : [];

const formatParties = (value: unknown) =>
  partiesList(value)
    .map(party => `${party.name}${party.role ? ` | ${party.role}` : ""}`)
    .join("\n");

const parseParties = (value: string): ContractFamilyPartyCorrection[] =>
  value
    .split("\n")
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const [name, ...roleParts] = line.split("|");
      const role = roleParts.join("|").trim();
      return { name: name.trim(), role: role || null };
    });

const parseLines = (value: string) =>
  value
    .split("\n")
    .map(line => line.trim())
    .filter(Boolean);

const defaultValues = (metadata: MetadataResponse | null): FormValues => ({
  documentName: metadata?.document_name ?? "",
  parties: formatParties(metadata?.parties_json),
  partyRoles: formatParties(metadata?.parties_role_json),
  executionDate: metadata?.execution_date ?? "",
  effectiveDate: metadata?.effective_date ?? "",
  amendsReference: metadata?.amends_reference ?? "",
  parentHints: stringList(metadata?.parent_hints_json).join("\n"),
  filenameHints: stringList(metadata?.filename_hints_json).join("\n"),
  multiDoc: metadata?.multi_doc === true ? "yes" : metadata?.multi_doc === false ? "no" : "unknown",
  comment: "",
});

export const MetadataEditorDialog: React.FC<Props> = ({ open, item, isSubmitting, onOpenChange, onSubmit }) => {
  const metadata = item?.metadata ?? null;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: defaultValues(null) });

  React.useEffect(() => {
    if (open) {
      reset(defaultValues(metadata));
    }
  }, [item?.document_id, metadata, open, reset]);

  const submit = async (values: FormValues) => {
    await onSubmit({
      document_name: values.documentName.trim() || null,
      parties_json: parseParties(values.parties),
      parties_role_json: parseParties(values.partyRoles),
      execution_date: values.executionDate || null,
      effective_date: values.effectiveDate || null,
      amends_reference: values.amendsReference.trim() || null,
      parent_hints_json: parseLines(values.parentHints),
      filename_hints_json: parseLines(values.filenameHints),
      multi_doc: values.multiDoc === "unknown" ? null : values.multiDoc === "yes",
      comment: values.comment.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>Edit extracted metadata</DialogTitle>
          <DialogDescription>
            Corrections are recorded as manual audit decisions. The original model response remains unchanged.
          </DialogDescription>
        </DialogHeader>

        <form className="grid gap-4" onSubmit={handleSubmit(submit)}>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="grid gap-1 text-xs font-medium text-slate-700 md:col-span-2">
              Document name
              <Input maxLength={1000} {...register("documentName")} />
            </label>
            <label className="grid gap-1 text-xs font-medium text-slate-700">
              Execution date
              <Input type="date" {...register("executionDate")} />
            </label>
            <label className="grid gap-1 text-xs font-medium text-slate-700">
              Effective date
              <Input type="date" {...register("effectiveDate")} />
            </label>
            <label className="grid gap-1 text-xs font-medium text-slate-700 md:col-span-2">
              Amends reference
              <Input maxLength={8000} {...register("amendsReference")} />
            </label>
            <label className="grid gap-1 text-xs font-medium text-slate-700">
              Parties
              <Textarea
                className="min-h-28 font-mono text-xs"
                placeholder="Party name | role"
                aria-invalid={errors.parties ? "true" : "false"}
                {...register("parties", { maxLength: 100000 })}
              />
            </label>
            <label className="grid gap-1 text-xs font-medium text-slate-700">
              Party roles
              <Textarea
                className="min-h-28 font-mono text-xs"
                placeholder="Party name | role"
                {...register("partyRoles", { maxLength: 100000 })}
              />
            </label>
            <label className="grid gap-1 text-xs font-medium text-slate-700">
              Parent hints
              <Textarea className="min-h-24 text-xs" placeholder="One hint per line" {...register("parentHints")} />
            </label>
            <label className="grid gap-1 text-xs font-medium text-slate-700">
              Filename hints
              <Textarea className="min-h-24 text-xs" placeholder="One hint per line" {...register("filenameHints")} />
            </label>
            <label className="grid gap-1 text-xs font-medium text-slate-700">
              Multi-document file
              <select
                className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900"
                {...register("multiDoc")}
              >
                <option value="unknown">Unknown</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="grid gap-1 text-xs font-medium text-slate-700 md:col-span-2">
              Audit comment
              <Textarea className="min-h-20 text-sm" maxLength={2000} {...register("comment")} />
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving…" : "Save corrections"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
